/**
 * Data-entry field widgets: Rich Text (contenteditable) and Markdown (write/preview).
 * Exposes idempotent mount() for forms, record modals, and grid inline editors.
 */
import { wireRichText } from '../shared/richtext-core';
import { mountAiFieldCharts } from './ai-field-charts';

declare global {
    interface Window {
        SpDataEntryFields?: { mount(root?: ParentNode): void };
    }
}

function antiforgeryToken(root: ParentNode): string {
    const scope = root instanceof Document ? root : root.ownerDocument ?? document;
    const input = scope.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]');
    return input?.value ?? '';
}

function syncHidden(hidden: HTMLInputElement, value: string): void {
    hidden.value = value;
    hidden.dispatchEvent(new Event('change', { bubbles: true }));
}

function placeRichTextModeTabs(field: HTMLElement, mode: 'visual' | 'source'): void {
    const tabs = field.querySelector<HTMLElement>('.sp-richtext-mode-tabs');
    if (!tabs) return;

    if (mode === 'visual') {
        const toolbar = field.querySelector<HTMLElement>('.sp-richtext-toolbar--inline');
        if (!toolbar) return;
        let trailing = toolbar.querySelector<HTMLElement>('.sp-richtext-toolbar__trailing');
        if (!trailing) {
            trailing = document.createElement('div');
            trailing.className = 'sp-richtext-toolbar__trailing';
            toolbar.appendChild(trailing);
        }
        trailing.appendChild(tabs);
        return;
    }

    const sourcePanel = field.querySelector<HTMLElement>('.sp-richtext-source');
    if (sourcePanel) sourcePanel.insertBefore(tabs, sourcePanel.firstChild);
}

function syncRichTextSourceHeight(field: HTMLElement): void {
    const visualPanel = field.querySelector<HTMLElement>('.sp-richtext-visual');
    const sourceInput = field.querySelector<HTMLTextAreaElement>('.sp-richtext-source-input');
    if (!visualPanel || !sourceInput) return;

    const visualHeight = visualPanel.offsetHeight;
    if (visualHeight > 0) {
        sourceInput.style.minHeight = `${visualHeight}px`;
        sourceInput.style.height = `${visualHeight}px`;
    }
}

function clearRichTextSourceHeight(field: HTMLElement): void {
    const sourceInput = field.querySelector<HTMLTextAreaElement>('.sp-richtext-source-input');
    if (!sourceInput) return;
    sourceInput.style.minHeight = '';
    sourceInput.style.height = '';
}

function mountRichTextFields(root: ParentNode): void {
    root.querySelectorAll<HTMLElement>('.sp-richtext-field:not([data-sp-mounted])').forEach((field) => {
        field.dataset.spMounted = '1';
        const hidden = field.querySelector<HTMLInputElement>('.sp-richtext-value');
        const editor = field.querySelector<HTMLElement>('[data-sp-richtext]');
        const sourcePanel = field.querySelector<HTMLElement>('.sp-richtext-source');
        const sourceInput = field.querySelector<HTMLTextAreaElement>('.sp-richtext-source-input');
        const visualPanel = field.querySelector<HTMLElement>('.sp-richtext-visual');
        if (!hidden || !editor || !sourcePanel || !sourceInput || !visualPanel) return;

        wireRichText(editor, {
            toolbarMode: 'inline',
            onInput: (html) => syncHidden(hidden, html),
        });
        syncHidden(hidden, editor.innerHTML);
        placeRichTextModeTabs(field, 'visual');

        field.querySelectorAll<HTMLButtonElement>('[data-sp-richtext-mode]').forEach((btn) => {
            btn.addEventListener('click', (ev) => {
                ev.preventDefault();
                const mode = btn.getAttribute('data-sp-richtext-mode');
                const isSource = mode === 'source';
                field.querySelectorAll('[data-sp-richtext-mode]').forEach((b) => {
                    b.classList.toggle('is-active', b === btn);
                    b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
                });
                if (isSource) {
                    syncRichTextSourceHeight(field);
                    sourceInput.value = hidden.value;
                } else {
                    clearRichTextSourceHeight(field);
                }
                // The partials hide panels with Tailwind's `hidden` (Bootstrap's d-none is gone).
                sourcePanel.classList.toggle('hidden', !isSource);
                visualPanel.classList.toggle('hidden', isSource);
                placeRichTextModeTabs(field, isSource ? 'source' : 'visual');
                if (isSource) {
                    sourceInput.focus();
                } else {
                    editor.innerHTML = hidden.value;
                    editor.focus();
                }
            });
        });

        sourceInput.addEventListener('input', () => syncHidden(hidden, sourceInput.value));
    });
}

async function fetchMarkdownPreview(url: string, markdown: string, token: string): Promise<string> {
    const resp = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/html',
            'RequestVerificationToken': token,
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ markdown }),
    });
    if (!resp.ok) throw new Error('Preview failed');
    return resp.text();
}

function mountMarkdownFields(root: ParentNode): void {
    root.querySelectorAll<HTMLElement>('.sp-markdown-field:not([data-sp-mounted])').forEach((field) => {
        field.dataset.spMounted = '1';
        const previewUrl = field.getAttribute('data-preview-url') ?? '';
        const writePanel = field.querySelector<HTMLElement>('.sp-markdown-write');
        const previewPanel = field.querySelector<HTMLElement>('.sp-markdown-preview');
        const textarea = field.querySelector<HTMLTextAreaElement>('textarea');
        if (!writePanel || !previewPanel || !textarea || !previewUrl) return;

        let previewTimer: number | null = null;

        const renderPreview = () => {
            previewPanel.innerHTML = '<span class="text-muted-foreground small">Rendering…</span>';
            const token = antiforgeryToken(root);
            fetchMarkdownPreview(previewUrl, textarea.value, token)
                .then((html) => { previewPanel.innerHTML = html; })
                .catch(() => { previewPanel.innerHTML = '<span class="text-destructive small">Could not render preview.</span>'; });
        };

        field.querySelectorAll<HTMLButtonElement>('[data-sp-md-tab]').forEach((btn) => {
            btn.addEventListener('click', (ev) => {
                ev.preventDefault();
                const tab = btn.getAttribute('data-sp-md-tab');
                const isPreview = tab === 'preview';
                field.querySelectorAll('[data-sp-md-tab]').forEach((b) => {
                    b.classList.toggle('is-active', b === btn);
                    b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
                });
                writePanel.classList.toggle('hidden', isPreview);
                previewPanel.classList.toggle('hidden', !isPreview);
                if (isPreview) renderPreview();
            });
        });

        textarea.addEventListener('input', () => {
            if (previewPanel.classList.contains('hidden')) return;
            if (previewTimer) window.clearTimeout(previewTimer);
            previewTimer = window.setTimeout(renderPreview, 300);
        });
    });
}

function mount(root: ParentNode = document): void {
    mountRichTextFields(root);
    mountMarkdownFields(root);
    mountAiFieldCharts(root);
}

window.SpDataEntryFields = { mount };

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount());
} else {
    mount();
}

document.addEventListener('sp:record-modal:loaded', (ev) => {
    if (ev.target instanceof HTMLElement) mount(ev.target);
});
